import {
  pgTable,
  text,
  serial,
  varchar,
  boolean,
  timestamp,
  integer,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const audienceSchema = z.enum(["all", "broker", "employer", "employee"]);
export type Audience = z.infer<typeof audienceSchema>;

export const articleStatusSchema = z.enum(["draft", "published", "archived"]);
export type ArticleStatus = z.infer<typeof articleStatusSchema>;

// ── Categories ──────────────────────────────────────────────────────────────

export const kbCategories = pgTable(
  "kb_categories",
  {
    id: serial("id").primaryKey(),
    name: varchar("name", { length: 100 }).notNull(),
    slug: varchar("slug", { length: 100 }).notNull().unique(),
    description: text("description"),
    // Lucide icon name shown in nav/lists
    icon: varchar("icon", { length: 50 }).notNull().default("BookOpen"),
    targetAudience: varchar("target_audience", { length: 20 }).notNull().default("all"),
    sortOrder: integer("sort_order").notNull().default(0),
    status: varchar("status", { length: 20 }).notNull().default("active"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    slugIdx: index("kb_categories_slug_idx").on(t.slug),
    audienceIdx: index("kb_categories_audience_idx").on(t.targetAudience),
  })
);

export const insertKbCategorySchema = createInsertSchema(kbCategories)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    targetAudience: audienceSchema.default("all"),
    name: z.string().min(1).max(100),
    slug: z.string().min(1).max(100).regex(/^[a-z0-9-]+$/, "Slug must be lowercase letters, numbers, and hyphens only"),
  });

export type KbCategory = typeof kbCategories.$inferSelect;
export type InsertKbCategory = z.infer<typeof insertKbCategorySchema>;

// ── Articles ────────────────────────────────────────────────────────────────

export const kbArticles = pgTable(
  "kb_articles",
  {
    id: serial("id").primaryKey(),
    // onDelete: set null so deleting a category leaves articles as "Uncategorized" rather than cascading
    categoryId: integer("category_id").references(() => kbCategories.id, { onDelete: "set null" }),
    title: varchar("title", { length: 300 }).notNull(),
    slug: varchar("slug", { length: 300 }).notNull().unique(),
    // HTML output from RichTextEditor; sanitized on save AND render
    content: text("content"),
    excerpt: text("excerpt"),
    targetAudience: varchar("target_audience", { length: 20 }).notNull().default("all"),
    status: varchar("status", { length: 20 }).notNull().default("draft"),
    tags: text("tags").array(),
    featured: boolean("featured").notNull().default(false),
    sortOrder: integer("sort_order").notNull().default(0),
    authorId: varchar("author_id", { length: 200 }),
    // Stamped on first transition to 'published'; preserved across subsequent edits
    publishedAt: timestamp("published_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    categoryIdx: index("kb_articles_category_idx").on(t.categoryId),
    slugIdx: index("kb_articles_slug_idx").on(t.slug),
    statusIdx: index("kb_articles_status_idx").on(t.status),
    audienceIdx: index("kb_articles_audience_idx").on(t.targetAudience),
  })
);

export const insertKbArticleSchema = createInsertSchema(kbArticles)
  .omit({ id: true, createdAt: true, updatedAt: true, publishedAt: true })
  .extend({
    targetAudience: audienceSchema.default("all"),
    status: articleStatusSchema.default("draft"),
    title: z.string().min(1).max(300),
    slug: z.string().min(1).max(300).regex(/^[a-z0-9-]+$/, "Slug must be lowercase letters, numbers, and hyphens only"),
    tags: z.array(z.string()).optional().nullable(),
  });

export type KbArticle = typeof kbArticles.$inferSelect;
export type InsertKbArticle = z.infer<typeof insertKbArticleSchema>;

// ── AI Training Snippets (consumed by chat widget, managed by AI Training page) ──

export const aiTrainingSnippets = pgTable("ai_training_snippets", {
  id: serial("id").primaryKey(),
  title: varchar("title", { length: 200 }).notNull(),
  content: text("content").notNull(),
  // Free-form categorization (general, plans, enrollment, etc.). Surfaced as filter pills.
  category: varchar("category", { length: 50 }).notNull().default("general"),
  targetAudience: varchar("target_audience", { length: 20 }).notNull().default("all"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertAiTrainingSnippetSchema = createInsertSchema(aiTrainingSnippets)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    targetAudience: audienceSchema.default("all"),
    title: z.string().min(1).max(200),
    content: z.string().min(1),
  });

export type AiTrainingSnippet = typeof aiTrainingSnippets.$inferSelect;
export type InsertAiTrainingSnippet = z.infer<typeof insertAiTrainingSnippetSchema>;
